import { supabase } from './supabase';
import type { Events } from './eventTypes';
import { API_BASE_URL } from './apiConfig';
import { installTravelerSession } from './travelerSession';

// Re-export event types for convenience
export type {
  Events,
  Flight,
  FlightSegment,
  FlightPassenger,
  Hotel,
  HotelRoom,
  Cruise,
  CruisePort,
  CruiseCabin,
  Activity,
  Dining,
  Transportation,
  Insurance,
  OtherEvent
} from './eventTypes';

export interface ItineraryFromDB {
  itinerary_id: string;
  itinerary_title: string;
  date_start: string;
  date_end: string;
  guest_number: number;
  is_published: boolean;
  locations: any;
  cover_image?: string | null;
  pre_travel_notes?: string | null;
  post_travel_notes?: string | null;
  day_subtitles?: Record<string, string> | null;
  language?: 'en' | 'es';
  guide_item_ids?: string[];
  template_ids?: string[];
  created_at: string;
  updated_at: string;
  agent_id: string;
  // Joined data (all optional - may not be fetched in list view for performance)
  agent?: {
    first_name: string;
    last_name: string;
    agency_id?: string;
  };
  collaborators?: Array<{
    user_id: string;
    profiles?: {
      first_name: string;
      last_name: string;
    };
  }>;
  travelers?: Array<{
    first_name: string;
    last_name: string;
  }>;
}

export interface PaginatedItineraries {
  data: ItineraryFromDB[];
  count: number;
  hasMore: boolean;
}

const PAGE_SIZE = 20;

// List cards do not need notes, day subtitles, or attached guide/template data.
const ITINERARY_LIST_COLUMNS = `
  itinerary_id, itinerary_title, date_start, date_end, guest_number,
  is_published, locations, cover_image, created_at, updated_at, agent_id
`;

/**
 * Fetch itineraries owned by the current user
 * Optimized version: only fetch essential data for list view
 */
export async function fetchMyItineraries(
  userId: string,
  page: number = 0
): Promise<PaginatedItineraries> {
  const from = page * PAGE_SIZE;
  const to = from + PAGE_SIZE - 1;

  // Simplified query - only fetch agent name from profiles
  const { data, error, count } = await supabase
    .from('itineraries')
    .select(`
      ${ITINERARY_LIST_COLUMNS},
      agent:profiles!agent_id(first_name, last_name)
    `, { count: 'exact' })
    .eq('agent_id', userId)
    .order('date_start', { ascending: false })
    .range(from, to);

  if (error) {
    throw error;
  }

  // If we need collaborators and travelers, fetch them separately for better performance
  // This can be done on-demand when viewing individual itineraries
  return {
    data: data || [],
    count: count || 0,
    hasMore: (count || 0) > to + 1,
  };
}

/**
 * Fetch itineraries where the current user is a collaborator
 * Optimized version: single query using join
 */
export async function fetchCollaboratedItineraries(
  userId: string,
  page: number = 0
): Promise<PaginatedItineraries> {
  const from = page * PAGE_SIZE;
  const to = from + PAGE_SIZE - 1;

  // Use a single optimized query with inner join
  const { data, error, count } = await supabase
    .from('itineraries')
    .select(`
      ${ITINERARY_LIST_COLUMNS},
      agent:profiles!agent_id(first_name, last_name),
      itinerary_collaborators!inner(user_id)
    `, { count: 'exact' })
    .eq('itinerary_collaborators.user_id', userId)
    .order('date_start', { ascending: false })
    .range(from, to);

  if (error) {
    throw error;
  }

  // For collaborated itineraries, fetch missing agent names for external agents
  // (agents from other agencies may be blocked by RLS)
  if (data && data.length > 0) {
    const missingAgentIds = data
      .filter(itinerary => !itinerary.agent && itinerary.agent_id)
      .map(itinerary => itinerary.agent_id);

    if (missingAgentIds.length > 0) {
      try {
        const response = await fetch(`${API_BASE_URL}/api/profiles/batch`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            user_ids: missingAgentIds
          })
        });

        if (response.ok) {
          const result = await response.json();
          const profilesMap = new Map(
            result.profiles.map((p: any) => [p.id, p])
          );

          // Patch the data with fetched profiles
          data.forEach(itinerary => {
            if (!itinerary.agent && itinerary.agent_id) {
              const profile = profilesMap.get(itinerary.agent_id);
              if (profile) {
                itinerary.agent = {
                  first_name: profile.first_name,
                  last_name: profile.last_name
                };
              }
            }
          });
        }
      } catch (error) {
        // Don't fail the whole operation, just leave as Unknown
      }
    }
  }

  return {
    data: data || [],
    count: count || 0,
    hasMore: (count || 0) > to + 1,
  };
}

/**
 * Fetch all itineraries in the agency
 * Optimized version: simplified query with agency filtering
 */
export async function fetchAgencyItineraries(
  agencyId: string,
  page: number = 0
): Promise<PaginatedItineraries> {
  const from = page * PAGE_SIZE;
  const to = from + PAGE_SIZE - 1;

  // Filter by agent's agency_id - only show itineraries created by agents in the same agency
  // First, get all agent IDs from the same agency
  const { data: agentsData, error: agentsError } = await supabase
    .from('profiles')
    .select('id')
    .eq('agency_id', agencyId);

  if (agentsError) {
    throw agentsError;
  }

  const agentIds = agentsData?.map(agent => agent.id) || [];

  // Now fetch itineraries where agent_id is in the list of agency agents
  const { data, error, count } = await supabase
    .from('itineraries')
    .select(`
      ${ITINERARY_LIST_COLUMNS},
      agent:profiles!agent_id(first_name, last_name, agency_id)
    `, { count: 'exact' })
    .in('agent_id', agentIds)
    .order('date_start', { ascending: false })
    .range(from, to);

  if (error) {
    throw error;
  }

  return {
    data: data || [],
    count: count || 0,
    hasMore: (count || 0) > to + 1,
  };
}

/**
 * Helper to determine itinerary status based on dates
 */
export function getItineraryStatus(startDate: string, endDate: string): 'current' | 'past' | 'upcoming' {
  const now = new Date();
  const start = new Date(startDate);
  const end = new Date(endDate);

  if (now >= start && now <= end) {
    return 'current';
  } else if (now > end) {
    return 'past';
  } else {
    return 'upcoming';
  }
}

/**
 * Helper to calculate number of days between two dates (INCLUSIVE)
 * March 26 to March 28 = 3 days (26, 27, 28)
 */
export function calculateDays(startDate: string, endDate: string): number {
  // Parse dates explicitly as local dates (YYYY-MM-DD format)
  // This avoids timezone issues
  const startParts = startDate.split('-').map(Number);
  const endParts = endDate.split('-').map(Number);

  const start = new Date(startParts[0], startParts[1] - 1, startParts[2]);
  const end = new Date(endParts[0], endParts[1] - 1, endParts[2]);

  // Calculate difference in milliseconds
  const diffTime = end.getTime() - start.getTime();

  // Convert to days and add 1 to include both start and end dates
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

  return diffDays + 1; // +1 to include both endpoints
}

/**
 * Transform database itinerary to frontend format
 */
export function transformItinerary(dbItinerary: ItineraryFromDB) {
  const locations = dbItinerary.locations || [];
  const firstLocation = Array.isArray(locations) && locations.length > 0 ? locations[0] : null;
  const locationCount = Array.isArray(locations) ? locations.length : 0;

  // Extract all location names and countries for searching
  const allLocationNames = Array.isArray(locations)
    ? locations.map((loc: any) => loc.name).filter(Boolean).join(' ')
    : '';
  const allCountries = Array.isArray(locations)
    ? locations.map((loc: any) => loc.country).filter(Boolean).join(' ')
    : '';

  return {
    id: dbItinerary.itinerary_id,
    name: dbItinerary.itinerary_title,
    destination: firstLocation?.name || 'Unknown',
    country: firstLocation?.country || 'Unknown',
    locationCount: locationCount,
    allLocations: allLocationNames,
    allCountries: allCountries,
    // Parse dates as local time to avoid timezone shift (append T00:00:00)
    startDate: new Date(dbItinerary.date_start + 'T00:00:00'),
    endDate: new Date(dbItinerary.date_end + 'T00:00:00'),
    status: getItineraryStatus(dbItinerary.date_start, dbItinerary.date_end),
    days: calculateDays(dbItinerary.date_start, dbItinerary.date_end),
    image: dbItinerary.cover_image || '',
    guests: dbItinerary.guest_number,
    owner_id: dbItinerary.agent_id,
    owner_name: dbItinerary.agent ? `${dbItinerary.agent.first_name} ${dbItinerary.agent.last_name}` : 'Unknown',
    collaborators: dbItinerary.collaborators?.map(c => ({
      id: c.user_id,
      name: `${c.profiles.first_name} ${c.profiles.last_name}`,
    })) || [],
    travelers: dbItinerary.travelers?.map(t => `${t.first_name} ${t.last_name}`.trim()) || [],
    agency_id: '', // TODO: Add when agency support is added
  };
}

/**
 * Create a new itinerary
 */
export interface CreateItineraryInput {
  itinerary_title: string;
  date_start: string; // Format: YYYY-MM-DD
  date_end: string; // Format: YYYY-MM-DD
  guest_number: number;
  locations?: any[]; // Array of location objects from Google Places API
  cover_image?: string;
  agent_id: string;
  language?: 'en' | 'es'; // Language preference for itinerary content
  guide_item_ids?: string[]; // Array of guide item UUIDs linked to this itinerary
  template_ids?: string[]; // Array of template UUIDs linked to this itinerary
}

export async function createItinerary(input: CreateItineraryInput): Promise<{
  data: ItineraryFromDB | null;
  error: any;
}> {
  // Debug: Check current auth state
  const { data: { user } } = await supabase.auth.getUser();

  const { data, error } = await supabase
    .from('itineraries')
    .insert({
      itinerary_title: input.itinerary_title,
      date_start: input.date_start,
      date_end: input.date_end,
      guest_number: input.guest_number,
      locations: input.locations || [],
      cover_image: input.cover_image || null,
      agent_id: input.agent_id,
      language: input.language || 'en',
      is_published: false,
      guide_item_ids: input.guide_item_ids || [],
      template_ids: input.template_ids || [],
    })
    .select(`
      *,
      agent:profiles!agent_id(first_name, last_name)
    `)
    .single();

  return { data, error };
}

/**
 * Update an existing itinerary
 */
export interface UpdateItineraryInput {
  itinerary_title?: string;
  date_start?: string; // Format: YYYY-MM-DD
  date_end?: string; // Format: YYYY-MM-DD
  guest_number?: number;
  locations?: any[]; // Array of location objects from Google Places API
  cover_image?: string | null;
  is_published?: boolean;
  pre_travel_notes?: string | null;
  post_travel_notes?: string | null;
  day_subtitles?: Record<string, string> | null;
  language?: 'en' | 'es'; // Language preference for itinerary content
  guide_item_ids?: string[]; // Array of guide item UUIDs linked to this itinerary
  template_ids?: string[]; // Array of template UUIDs linked to this itinerary
}

export async function updateItinerary(
  itinerary_id: string,
  updates: UpdateItineraryInput
): Promise<{
  data: ItineraryFromDB | null;
  error: any;
}> {
  const { data, error } = await supabase
    .from('itineraries')
    .update(updates)
    .eq('itinerary_id', itinerary_id)
    .select(`
      *,
      agent:profiles!agent_id(first_name, last_name)
    `)
    .single();

  if (error) {
  }

  return { data, error };
}

export async function fetchItinerary(
  itinerary_id: string
): Promise<{
  data: ItineraryFromDB | null;
  error: any;
}> {
  const { data, error } = await supabase
    .from('itineraries')
    .select('*')
    .eq('itinerary_id', itinerary_id)
    .single();

  if (error) {
  }

  return { data, error };
}

/**
 * Delete an itinerary and all related data
 *
 * Manually deletes:
 * - Grandchild records (flight_segments, flight_passengers, hotel_rooms, cruise_cabins, cruise_ports)
 * - Child event records (flights, hotels, cruises, activities, transportation, insurance, other_events, dining_events)
 * - Travelers (removes itinerary_id from array, deletes traveler if array becomes empty)
 * - Collaborators
 * - Storage files (documents, images)
 * - Document records
 *
 * Automatically deleted via CASCADE:
 * - itinerary_share_tokens
 * - itinerary_embeddings
 */
export async function deleteItinerary(
  itinerary_id: string
): Promise<{
  success: boolean;
  error: any;
}> {
  try {
    // ===== 1. GET PARENT IDS FOR GRANDCHILD DELETION =====
    const [flightsResult, hotelsResult, cruisesResult] = await Promise.all([
      supabase.from('flights').select('flight_id').eq('itinerary_id', itinerary_id),
      supabase.from('hotels').select('hotel_id').eq('itinerary_id', itinerary_id),
      supabase.from('cruises').select('cruise_id').eq('itinerary_id', itinerary_id),
    ]);

    const flightIds = flightsResult.data?.map(f => f.flight_id) || [];
    const hotelIds = hotelsResult.data?.map(h => h.hotel_id) || [];
    const cruiseIds = cruisesResult.data?.map(c => c.cruise_id) || [];

    // ===== 2. DELETE GRANDCHILD RECORDS IN PARALLEL =====
    const grandchildDeletes: Promise<any>[] = [];

    if (flightIds.length > 0) {
      grandchildDeletes.push(
        supabase.from('flight_passengers').delete().in('flight_id', flightIds),
        supabase.from('flight_segments').delete().in('flight_id', flightIds)
      );
    }

    if (hotelIds.length > 0) {
      grandchildDeletes.push(
        supabase.from('hotel_rooms').delete().in('hotel_id', hotelIds)
      );
    }

    if (cruiseIds.length > 0) {
      grandchildDeletes.push(
        supabase.from('cruise_cabins').delete().in('cruise_id', cruiseIds),
        supabase.from('cruise_ports').delete().in('cruise_id', cruiseIds)
      );
    }

    if (grandchildDeletes.length > 0) {
      await Promise.all(grandchildDeletes);
    }

    // ===== 3. DELETE CHILD EVENT RECORDS IN PARALLEL =====
    await Promise.all([
      supabase.from('flights').delete().eq('itinerary_id', itinerary_id),
      supabase.from('hotels').delete().eq('itinerary_id', itinerary_id),
      supabase.from('cruises').delete().eq('itinerary_id', itinerary_id),
      supabase.from('activities').delete().eq('itinerary_id', itinerary_id),
      supabase.from('transportation').delete().eq('itinerary_id', itinerary_id),
      supabase.from('insurance').delete().eq('itinerary_id', itinerary_id),
      supabase.from('other_events').delete().eq('itinerary_id', itinerary_id),
      supabase.from('dining_events').delete().eq('itinerary_id', itinerary_id),
    ]);

    // ===== 4. HANDLE TRAVELERS (ARRAY-BASED) =====
    // Travelers store itinerary_ids as an array - remove this id, delete if empty
    const { data: travelers } = await supabase
      .from('itinerary_travelers')
      .select('traveler_id, itinerary_ids')
      .contains('itinerary_ids', [itinerary_id]);

    if (travelers && travelers.length > 0) {
      const travelerUpdates: Promise<any>[] = [];

      for (const traveler of travelers) {
        const updatedIds = (traveler.itinerary_ids || []).filter(
          (id: string) => id !== itinerary_id
        );

        if (updatedIds.length === 0) {
          // Array empty - delete the traveler
          travelerUpdates.push(
            supabase.from('itinerary_travelers').delete().eq('traveler_id', traveler.traveler_id)
          );
        } else {
          // Remove itinerary_id from array
          travelerUpdates.push(
            supabase.from('itinerary_travelers').update({ itinerary_ids: updatedIds }).eq('traveler_id', traveler.traveler_id)
          );
        }
      }

      await Promise.all(travelerUpdates);
    }

    // ===== 5. DELETE COLLABORATORS =====
    await supabase
      .from('itinerary_collaborators')
      .delete()
      .eq('itinerary_id', itinerary_id);

    // ===== 6. DELETE STORAGE FILES =====
    // Get document URLs and delete from storage
    const { data: documents } = await supabase
      .from('itinerary_documents')
      .select('document_url')
      .eq('itinerary_id', itinerary_id);

    if (documents && documents.length > 0) {
      const filePaths = documents
        .map(doc => {
          if (!doc.document_url) return null;
          const match = doc.document_url.match(/itinerary-documents\/(.+)$/);
          return match ? match[1] : null;
        })
        .filter(Boolean) as string[];

      if (filePaths.length > 0) {
        await supabase.storage.from('itinerary-documents').remove(filePaths);
      }
    }

    // Delete document records
    await supabase
      .from('itinerary_documents')
      .delete()
      .eq('itinerary_id', itinerary_id);

    // Delete images from storage
    const { data: imageFiles } = await supabase.storage
      .from('itinerary-images')
      .list(itinerary_id);

    if (imageFiles && imageFiles.length > 0) {
      const imagePaths = imageFiles.map(file => `${itinerary_id}/${file.name}`);
      await supabase.storage.from('itinerary-images').remove(imagePaths);
    }

    // ===== 7. DELETE MAIN ITINERARY =====
    // CASCADE will auto-delete: itinerary_share_tokens, itinerary_embeddings
    const { error: deleteError } = await supabase
      .from('itineraries')
      .delete()
      .eq('itinerary_id', itinerary_id);

    if (deleteError) {
      return { success: false, error: deleteError };
    }

    return { success: true, error: null };
  } catch (error) {
    return { success: false, error };
  }
}

/**
 * Delete events that START in days that were shortened from the itinerary
 * This is called when the itinerary date range is changed (start or end date)
 */
export async function deleteEventsOutsideDateRange(
  itinerary_id: string,
  start_date: string,
  end_date: string
): Promise<{
  success: boolean;
  error: any;
}> {
  try {
    const startDate = new Date(start_date);
    const endDate = new Date(end_date);

    // Helper function to check if a date is BEFORE the new start date
    const startsBeforeStartDate = (dateStr: string | null) => {
      if (!dateStr) return false;
      const date = new Date(dateStr);
      return date < startDate;
    };

    // Helper function to check if a date is AFTER the new end date
    const startsAfterEndDate = (dateStr: string | null) => {
      if (!dateStr) return false;
      const date = new Date(dateStr);
      return date > endDate;
    };

    // ===== HANDLE START DATE CHANGES =====
    // Delete events that START before the new start date

    // Delete flights that START before the new start date (check first segment's departure_date)
    const { data: flightsForStart } = await supabase
      .from('flights')
      .select('flight_id')
      .eq('itinerary_id', itinerary_id);

    if (flightsForStart) {
      for (const flight of flightsForStart) {
        // Fetch first segment for this flight (ordered by segment_order)
        const { data: segments } = await supabase
          .from('flight_segments')
          .select('segment_id, departure_date')
          .eq('flight_id', flight.flight_id)
          .order('segment_order', { ascending: true })
          .limit(1);

        const firstSegment = segments?.[0];
        if (firstSegment && startsBeforeStartDate(firstSegment.departure_date)) {
          // Delete entire flight if first segment starts before start date
          await supabase
            .from('flights')
            .delete()
            .eq('flight_id', flight.flight_id);
        } else {
          // Otherwise, delete individual segments that start before start date
          await supabase
            .from('flight_segments')
            .delete()
            .eq('flight_id', flight.flight_id)
            .lt('departure_date', start_date);
        }
      }
    }

    // Delete hotels that START before the new start date (check checkin_date)
    await supabase
      .from('hotels')
      .delete()
      .eq('itinerary_id', itinerary_id)
      .lt('checkin_date', start_date);

    // Delete cruises that START before the new start date (check embarkation_date)
    const { data: cruisesForStart } = await supabase
      .from('cruises')
      .select('cruise_id, embarkation_date')
      .eq('itinerary_id', itinerary_id);

    if (cruisesForStart) {
      for (const cruise of cruisesForStart) {
        if (startsBeforeStartDate(cruise.embarkation_date)) {
          // Delete entire cruise if embarkation starts before start date
          await supabase
            .from('cruises')
            .delete()
            .eq('cruise_id', cruise.cruise_id);
        } else {
          // Otherwise, delete individual port calls that have arrival_date (embark) before start date
          await supabase
            .from('cruise_ports')
            .delete()
            .eq('cruise_id', cruise.cruise_id)
            .lt('arrival_date', start_date);
        }
      }
    }

    // Delete transportation that STARTS before the new start date
    const { data: transportationForStart } = await supabase
      .from('transportation')
      .select('transportation_id, transportation_type, departure_date, pickup_date')
      .eq('itinerary_id', itinerary_id);

    if (transportationForStart) {
      for (const transport of transportationForStart) {
        // For rental cars, use pickup_date; for others, use departure_date
        const transportStartDate = transport.transportation_type === 'rental'
          ? transport.pickup_date
          : transport.departure_date;

        if (startsBeforeStartDate(transportStartDate)) {
          await supabase
            .from('transportation')
            .delete()
            .eq('transportation_id', transport.transportation_id);
        }
      }
    }

    // Delete activities that START before the new start date (check activity_start_date)
    await supabase
      .from('activities')
      .delete()
      .eq('itinerary_id', itinerary_id)
      .lt('activity_start_date', start_date);

    // Delete insurance that STARTS before the new start date (check effective_date)
    await supabase
      .from('insurance')
      .delete()
      .eq('itinerary_id', itinerary_id)
      .lt('effective_date', start_date);

    // Delete other events that START before the new start date (check event_date)
    await supabase
      .from('other_events')
      .delete()
      .eq('itinerary_id', itinerary_id)
      .lt('event_date', start_date);

    // ===== HANDLE END DATE CHANGES =====
    // Delete events that START after the new end date

    // Delete flights that START after the new end date (check first segment's departure_date)
    const { data: flights } = await supabase
      .from('flights')
      .select('flight_id')
      .eq('itinerary_id', itinerary_id);

    if (flights) {
      for (const flight of flights) {
        // Fetch first segment for this flight (ordered by segment_order)
        const { data: segments } = await supabase
          .from('flight_segments')
          .select('segment_id, departure_date')
          .eq('flight_id', flight.flight_id)
          .order('segment_order', { ascending: true })
          .limit(1);

        const firstSegment = segments?.[0];
        if (firstSegment && startsAfterEndDate(firstSegment.departure_date)) {
          // Delete entire flight if first segment starts after end date
          await supabase
            .from('flights')
            .delete()
            .eq('flight_id', flight.flight_id);
        } else {
          // Otherwise, delete individual segments that start after end date
          await supabase
            .from('flight_segments')
            .delete()
            .eq('flight_id', flight.flight_id)
            .gt('departure_date', end_date);
        }
      }
    }

    // Delete hotels that START after the new end date (check checkin_date)
    await supabase
      .from('hotels')
      .delete()
      .eq('itinerary_id', itinerary_id)
      .gt('checkin_date', end_date);

    // Delete cruises that START after the new end date (check embarkation_date)
    const { data: cruises } = await supabase
      .from('cruises')
      .select('cruise_id, embarkation_date')
      .eq('itinerary_id', itinerary_id);

    if (cruises) {
      for (const cruise of cruises) {
        if (startsAfterEndDate(cruise.embarkation_date)) {
          // Delete entire cruise if embarkation starts after end date
          await supabase
            .from('cruises')
            .delete()
            .eq('cruise_id', cruise.cruise_id);
        } else {
          // Otherwise, delete individual port calls that have arrival_date (embark) after end date
          await supabase
            .from('cruise_ports')
            .delete()
            .eq('cruise_id', cruise.cruise_id)
            .gt('arrival_date', end_date);
        }
      }
    }

    // Delete transportation that STARTS after the new end date
    // For rental cars, check pickup_date; for others, check departure_date
    const { data: transportation } = await supabase
      .from('transportation')
      .select('transportation_id, transportation_type, departure_date, pickup_date')
      .eq('itinerary_id', itinerary_id);

    if (transportation) {
      for (const transport of transportation) {
        // For rental cars, use pickup_date; for others, use departure_date
        const startDate = transport.transportation_type === 'rental'
          ? transport.pickup_date
          : transport.departure_date;

        if (startsAfterEndDate(startDate)) {
          await supabase
            .from('transportation')
            .delete()
            .eq('transportation_id', transport.transportation_id);
        }
      }
    }

    // Delete activities that START after the new end date (check activity_start_date)
    await supabase
      .from('activities')
      .delete()
      .eq('itinerary_id', itinerary_id)
      .gt('activity_start_date', end_date);

    // Delete insurance that STARTS after the new end date (check effective_date)
    await supabase
      .from('insurance')
      .delete()
      .eq('itinerary_id', itinerary_id)
      .gt('effective_date', end_date);

    // Delete other events that START after the new end date (check event_date)
    await supabase
      .from('other_events')
      .delete()
      .eq('itinerary_id', itinerary_id)
      .gt('event_date', end_date);

    // ===== NOW HANDLE END DATES =====
    // For events that were NOT deleted (start date is OK),
    // handle their end dates based on whether the field is required or optional

    // REQUIRED FIELDS - DELETE entire event/segment if end date is after new end date:

    // Delete hotels if checkout_date (REQUIRED) is after the new end date
    await supabase
      .from('hotels')
      .delete()
      .eq('itinerary_id', itinerary_id)
      .gt('checkout_date', end_date);

    // Delete flight segments if arrival_date (REQUIRED) is after the new end date
    // First get all flight IDs for this itinerary
    const { data: flightsData } = await supabase
      .from('flights')
      .select('flight_id')
      .eq('itinerary_id', itinerary_id);

    if (flightsData && flightsData.length > 0) {
      const flightIds = flightsData.map(f => f.flight_id);

      // Get all segments for these flights
      const { data: allSegments } = await supabase
        .from('flight_segments')
        .select('segment_id, flight_id, arrival_date')
        .in('flight_id', flightIds);

      if (allSegments) {
        for (const segment of allSegments) {
          if (startsAfterEndDate(segment.arrival_date)) {
            await supabase
              .from('flight_segments')
              .delete()
              .eq('segment_id', segment.segment_id);
          }
        }
      }
    }

    // Delete cruises if disembarkation_date (REQUIRED) is after the new end date
    await supabase
      .from('cruises')
      .delete()
      .eq('itinerary_id', itinerary_id)
      .gt('disembarkation_date', end_date);

    // Delete cruise ports if departure_date (REQUIRED) is after the new end date
    // First get all cruise IDs for this itinerary
    const { data: cruisesData } = await supabase
      .from('cruises')
      .select('cruise_id')
      .eq('itinerary_id', itinerary_id);

    if (cruisesData && cruisesData.length > 0) {
      const cruiseIds = cruisesData.map(c => c.cruise_id);

      // Get all ports for these cruises
      const { data: allPorts } = await supabase
        .from('cruise_ports')
        .select('port_id, cruise_id, departure_date')
        .in('cruise_id', cruiseIds);

      if (allPorts) {
        for (const port of allPorts) {
          if (startsAfterEndDate(port.departure_date)) {
            await supabase
              .from('cruise_ports')
              .delete()
              .eq('port_id', port.port_id);
          }
        }
      }
    }

    // OPTIONAL FIELDS - SET TO NULL if end date is after new end date:

    // Update activities: set activity_end_date to null if it's after the new end date (OPTIONAL)
    await supabase
      .from('activities')
      .update({ activity_end_date: null })
      .eq('itinerary_id', itinerary_id)
      .gt('activity_end_date', end_date);

    // Update transportation: set arrival_date and dropoff_date to null if after the new end date (OPTIONAL)
    await supabase
      .from('transportation')
      .update({ arrival_date: null })
      .eq('itinerary_id', itinerary_id)
      .gt('arrival_date', end_date);

    await supabase
      .from('transportation')
      .update({ dropoff_date: null })
      .eq('itinerary_id', itinerary_id)
      .gt('dropoff_date', end_date);

    // ===== CLEANUP DAY SUBTITLES =====
    // Remove day_subtitles entries that fall outside the new date range
    const { data: itinerary } = await supabase
      .from('itineraries')
      .select('day_subtitles')
      .eq('itinerary_id', itinerary_id)
      .single();

    if (itinerary?.day_subtitles && Object.keys(itinerary.day_subtitles).length > 0) {
      const cleanedSubtitles = Object.fromEntries(
        Object.entries(itinerary.day_subtitles).filter(([date]) =>
          date >= start_date && date <= end_date
        )
      );
      await supabase
        .from('itineraries')
        .update({ day_subtitles: cleanedSubtitles })
        .eq('itinerary_id', itinerary_id);
    }

    return { success: true, error: null };
  } catch (error) {
    return { success: false, error };
  }
}

/**
 * Publish an itinerary and generate share codes
 */
export interface PublishItineraryResponse {
  email_delivery_complete?: boolean;
  success: boolean;
  message: string;
  traveler_code: string;
  agent_code: string;
}

export async function publishItinerary(itinerary_id: string): Promise<PublishItineraryResponse> {
  try {
    // Get the current session to retrieve the access token
    const { data: sessionData, error: sessionError } = await supabase.auth.getSession();

    if (sessionError || !sessionData.session) {
      throw new Error('No active session found');
    }

    const accessToken = sessionData.session.access_token;

    // Call the backend API
    const response = await fetch(`${API_BASE_URL}/api/itineraries/${itinerary_id}/publish`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: 'Unknown error' }));
      throw new Error(errorData.detail || `Failed to publish itinerary: ${response.status}`);
    }

    const data: PublishItineraryResponse = await response.json();
    return data;
  } catch (error) {
    throw error;
  }
}

/**
 * Fetch share tokens for a published itinerary
 */
export interface ShareToken {
  traveler_code: string;
  agent_code: string;
}

export async function fetchShareTokens(itinerary_id: string): Promise<ShareToken | null> {
  try {
    const { data, error } = await supabase
      .from('itinerary_share_tokens')
      .select('token, type')
      .eq('itinerary_id', itinerary_id)
      .eq('is_active', true);

    if (error) {
      return null;
    }

    if (!data || data.length === 0) {
      return null;
    }

    // Extract traveler and agent codes
    const travelerToken = data.find(t => t.type === 'traveler');
    const agentToken = data.find(t => t.type === 'agent');

    if (!travelerToken || !agentToken) {
      return null;
    }

    return {
      traveler_code: travelerToken.token,
      agent_code: agentToken.token
    };
  } catch (error) {
    return null;
  }
}

/**
 * Verify access to an itinerary using code and email
 */
export interface VerifyAccessRequest {
  code: string;
  email: string;
}

export interface SessionUser {
  id: string;
  email: string;
  role?: string;
}

export interface SessionData {
  access_token: string;
  refresh_token: string;
  expires_in: number;
  expires_at?: number;
  token_type: string;
  user: SessionUser;
}

export interface VerifyAccessResponse {
  success: boolean;
  message: string;
  access_type?: string;
  user_id?: string;  // traveler_id for travelers
  auth_user_id?: string;  // auth.users ID for travelers
  session?: SessionData;  // Supabase session for travelers
}

export interface CheckAccessResponse {
  has_access: boolean;
  access_type?: string;
  user_id?: string;
  auth_user_id?: string;  // auth.users ID for travelers
}

/**
 * Check if user has valid access to an itinerary via HttpOnly cookie.
 * Used to determine if login form should be shown.
 *
 * Note: The Supabase session is created during verify-access and persisted
 * in localStorage by the Supabase client. This endpoint just validates the cookie.
 */
export async function checkItineraryAccess(itinerary_id: string): Promise<CheckAccessResponse> {
  try {
    const response = await fetch(
      `${API_BASE_URL}/api/itineraries/${itinerary_id}/check-access`,
      {
        method: 'GET',
        credentials: 'include', // Include HttpOnly cookies
      }
    );

    if (!response.ok) {
      return { has_access: false };
    }

    const data: CheckAccessResponse = await response.json();
    return data;
  } catch (error) {
    return { has_access: false };
  }
}

/**
 * Verify access to an itinerary using code and email.
 * On success, backend sets an itinerary cookie. Only dedicated traveler identities
 * may also receive a Supabase session; existing agency identities never do.
 */
export async function verifyItineraryAccess(
  itinerary_id: string,
  code: string,
  email: string
): Promise<VerifyAccessResponse> {
  try {
    const response = await fetch(
      `${API_BASE_URL}/api/itineraries/${itinerary_id}/verify-access`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include', // Allow backend to set HttpOnly cookie
        body: JSON.stringify({ code, email }),
      }
    );

    if (!response.ok) {
      // Try to parse error response, but fail gracefully
      const errorData = await response.json().catch(() => ({ detail: 'Invalid credentials. Please try again.' }));
      throw new Error(errorData.detail || 'Invalid credentials. Please try again.');
    }

    const data: VerifyAccessResponse = await response.json();

    // Dedicated travelers retain session-based app features. Preserve any
    // independent agency login; the itinerary cookie is sufficient for viewing.
    if (data.session) {
      await installTravelerSession(supabase, data.session);
    }

    return data;
  } catch (error) {
    // Log detailed error for debugging (only in console)

    // Return user-friendly message only
    if (error instanceof Error && error.message.includes('credentials')) {
      throw new Error(error.message);
    }
    throw new Error('Invalid credentials. Please try again.');
  }
}

/**
 * Fetch itinerary data for public view
 */
export interface TravelerData {
  traveler_id: string;
  full_name: string;
  email?: string;
}

export interface DocumentData {
  document_id: string;
  itinerary_id: string;
  document_name: string;
  document_type: string;
  document_url: string;
  file_size_bytes: number;
  uploaded_at: string;
}

export interface AgentData {
  agent_id: string;
  full_name: string;
  email?: string;
  phone?: string;
}

export interface InfoDocAttachmentView {
  id: string;
  info_doc_id: string;
  file_name: string;
  file_type: string | null;
  file_size: number | null;
  storage_path: string;
  display_order: number;
}

export interface InfoDocView {
  id: string;
  itinerary_id: string;
  title: string;
  description: string | null;
  display_order: number;
  created_at: string;
  updated_at: string;
  attachments: InfoDocAttachmentView[];
}

export interface GuideItemView {
  id: string;
  item_type: string;
  name: string;
  description?: string | null;
  address?: string | null;
  city?: string | null;
  country?: string | null;
  country_code?: string | null;
  google_place_id?: string | null;
  instagram_url?: string | null;
  website?: string | null;
  phone?: string | null;
  images?: string[];
  dining_type?: string | null;
  price_range?: string | null;
  metadata?: Record<string, unknown>;
  position?: number | null;
}

export interface GuideTemplateView {
  template_id: string;
  name: string;
  description?: string | null;
  cover_image?: string | null;
  items: GuideItemView[];
}

export interface ItineraryViewData {
  itinerary_id: string;
  itinerary_title: string;
  agency_id?: string;
  available_pdf_templates?: string[] | null;
  date_start: string;
  date_end: string;
  number_of_days: number;
  guest_number: number;
  locations?: Array<{ name?: string; city?: string; address?: string; [key: string]: any }>;
  cover_image_url?: string;
  pre_travel_notes?: string;
  post_travel_notes?: string;
  day_subtitles?: Record<string, string>;
  language?: 'en' | 'es';
  guide_item_ids?: string[];
  template_ids?: string[];
  agent?: AgentData;
  travelers: TravelerData[];
  documents: DocumentData[];
  info_docs: InfoDocView[];
  guide_items: GuideItemView[];
  templates: GuideTemplateView[];
  events: Events;
  style_settings?: Record<string, unknown>;
}

export async function fetchItineraryView(itinerary_id: string): Promise<ItineraryViewData> {
  try {
    // Build headers with authentication credentials
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    // Try to get Supabase session token (for authenticated agents)
    const { data: sessionData } = await supabase.auth.getSession();
    if (sessionData?.session?.access_token) {
      headers['Authorization'] = `Bearer ${sessionData.session.access_token}`;
    }

    // For travelers, the HttpOnly cookie is sent automatically via credentials: 'include'
    const response = await fetch(
      `${API_BASE_URL}/api/itineraries/${itinerary_id}/view`,
      {
        method: 'GET',
        headers,
        credentials: 'include', // Include HttpOnly cookies for traveler auth
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: 'Failed to fetch itinerary' }));
      // Include status code in error for proper handling
      const errorMessage = response.status === 401
        ? 'Authentication required. Please provide valid credentials.'
        : (errorData.detail || 'Failed to fetch itinerary');
      throw new Error(errorMessage);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    throw error;
  }
}

/**
 * Optimized builder data types - matches the SQL function output
 */
export interface BuilderTraveler {
  traveler_id: string;
  first_name: string;
  last_name: string;
  email: string;
}

export interface BuilderCollaborator {
  itinerary_id: string;
  user_id: string;
  role: string;
  added_by: string;
  email: string;
  first_name: string;
  last_name: string;
}

export interface BuilderDocument {
  document_id: string;
  itinerary_id: string;
  document_name: string;
  document_type: string;
  document_url: string;
  file_size_bytes: number;
  uploaded_at: string;
}

export interface BuilderAgent {
  agent_id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
}

export interface BuilderEvents {
  flights: any[];
  hotels: any[];
  cruises: any[];
  activities: any[];
  dining: any[];
  transportation: any[];
  insurance: any[];
  other_events: any[];
}

export interface ItineraryBuilderData {
  itinerary_id: string;
  itinerary_title: string;
  date_start: string;
  date_end: string;
  number_of_days: number;
  guest_number: number;
  locations: any[];
  cover_image_url: string | null;
  pre_travel_notes: string | null;
  post_travel_notes: string | null;
  day_subtitles: Record<string, string> | null;
  language: string;
  is_published: boolean;
  guide_item_ids: string[];
  template_ids: string[];
  agent: BuilderAgent | null;
  style_settings: any | null;
  travelers: BuilderTraveler[];
  collaborators: BuilderCollaborator[];
  documents: BuilderDocument[];
  events: BuilderEvents;
}

/**
 * Fetch all itinerary data for the Builder in a single optimized query.
 * Replaces 12+ individual fetch calls with one API call.
 */
export async function fetchItineraryBuilderData(itinerary_id: string, signal?: AbortSignal, expectedUserId?: string): Promise<ItineraryBuilderData | null> {
  try {
    // Get Supabase session token
    const { data: sessionData } = await supabase.auth.getSession();
    if (!sessionData?.session?.access_token) {
      return null;
    }

    // A navigation started under another identity must not reuse this response.
    if (expectedUserId && sessionData.session.user.id !== expectedUserId) return null;

    const response = await fetch(
      `${API_BASE_URL}/api/itineraries/${itinerary_id}/builder`,
      {
        method: 'GET',
        signal,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionData.session.access_token}`,
        },
      }
    );

    if (!response.ok) {
      // Don't expose error details to console in production
      return null;
    }

    const data = await response.json();
    return data;
  } catch {
    // Fail silently - caller handles null return
    return null;
  }
}
